import {I18n} from 'aws-amplify/utils';

import {DnsWriteErrorKind} from '../zones/types';

// FNP-1193 — WHAT THE HUMAN IS TOLD WHEN A CERTIFICATE WRITE IS REFUSED.
//
// ⚠️ THE CLASSIFIER IS REUSED, THE COPY IS NOT — the same split the records
// module made, for the same reason.
//
// `classifyWriteError` (zones/services/writeErrors) turns a GraphQL rejection
// back into a kind by reading the `KIND: ` message prefix the contract's own
// constructor builds, falling back to the class name in `errorType`. That work
// is about the API's error envelope, which is identical across these modules,
// so duplicating it here would be drift.
//
// The COPY is a different matter, and here it diverges more sharply than the
// records copy did from the zones copy — because the most likely refusal on
// this path has a cause that exists nowhere else on the platform.

type WriteErrorCopy = {
    title: string,
    action: string,
}

// Not a module-scope object literal: `I18n.get` must run at call time, after
// the language is configured, or every string is captured in the default locale
// at import.
export const certificateWriteErrorCopy = (kind: DnsWriteErrorKind): WriteErrorCopy => {

    switch (kind) {

        // ⚠️ THE ONE THIS MODULE CANNOT PREVENT, AND THE COPY EARNS ITS LENGTH.
        //
        // Every other module hides its write controls when the credential
        // cannot write, so PERMISSION_DENIED is the case that "should never
        // happen". Here it is a case that WILL happen to tenants who did
        // nothing wrong.
        //
        // `ProviderConnectionCapabilities` has no ACM field, so the console
        // cannot know before submitting whether the tenant's role carries
        // `acm:RequestCertificate`. Worse, the platform's own tenant-facing
        // instructions still describe a DNS-only role (the certificate decision
        // doc's "still open" item 1), so a tenant who followed them exactly
        // ends up with a connection that authenticates perfectly and cannot
        // issue.
        //
        // So this message NAMES THE TWO ACTIONS and says the grant belongs in
        // the tenant's OWN role. Without that, an AccessDenied at request time
        // reads as a Flexi bug and the tenant has no way to find the fix.
        case 'PERMISSION_DENIED':
            return {
                title: I18n.get('This connection is not permitted to issue certificates'),
                action: I18n.get('The credential is valid, but the role it assumes does not carry the certificate permissions. Certificates are issued in your own account under your own credential, so the grant belongs in your role rather than in Flexi: add acm:RequestCertificate and acm:DeleteCertificate to the role this connection assumes, then try again. Nothing has been changed. Keep both permissions in place — the provider re-checks the validation record for the life of the certificate, so removing them later breaks renewal silently.'),
            };

        case 'AUTHENTICATION_FAILED':
            return {
                title: I18n.get('The connection credential was rejected'),
                action: I18n.get('The token or role for this connection is expired or revoked. Reconnect the provider in Settings — retrying will not help. Nothing has been changed.'),
            };

        // ⚠️ NAMES THE CERTIFICATE-SHAPED CAUSES. The commonest by far is a
        // region that contradicts the target: a CloudFront certificate must be
        // issued in us-east-1, and supplying a different one is REFUSED rather
        // than silently overridden — deliberately, so a caller who believes
        // they chose eu-west-1 is told they did not.
        case 'VALIDATION_REJECTED':
            return {
                title: I18n.get('The request was rejected'),
                action: I18n.get('The provider or Flexi considers this request invalid. The usual causes are a hostname that is not a valid domain name, or a region that contradicts the target — a certificate for a CloudFront distribution must be issued in us-east-1, and naming a different region is refused rather than quietly corrected. Retrying the same values will not help; check the message below.'),
            };

        // ⚠️ NAMES THE ZONE, because that is what is missing.
        //
        // Requesting a certificate writes the validation record into the zone
        // that owns the hostname. If no connected zone owns it, there is
        // nowhere to write — and the fix is to add the zone, not to retry.
        case 'NOT_FOUND':
            return {
                title: I18n.get('Nothing here owns that hostname'),
                action: I18n.get('The validation record has to be written into the zone that owns this hostname, and no zone on this connection does. Check the hostname for a typo, and confirm the zone is connected — a certificate cannot be validated for a name whose DNS Flexi cannot reach.'),
            };

        case 'RATE_LIMITED':
            return {
                title: I18n.get('The provider is rate limiting requests'),
                action: I18n.get('This is temporary. Wait a moment and try again — nothing has been changed.'),
            };

        case 'PROVIDER_UNAVAILABLE':
            return {
                title: I18n.get('The provider could not be reached'),
                action: I18n.get('The provider is down or unreachable. This is temporary and retrying may work — nothing has been changed.'),
            };

        // ⚠️ CLAIMS NOTHING IT HAS NO EVIDENCE FOR, and on this path that
        // caution is not theoretical.
        //
        // Requesting a certificate is TWO steps — issue at the provider, then
        // write the validation record — so a failure between them leaves a real
        // certificate that this console has not listed. "Nothing has been
        // changed" would be a lie in exactly the case an operator most needs
        // the truth. Reload rather than retry: a blind retry could issue a
        // second certificate for the same name.
        default:
            return {
                title: I18n.get('The request did not complete'),
                action: I18n.get('No recognisable reason came back, and a certificate may have been issued before the failure. Reload the list to see the current state before trying again — retrying blindly can issue a second certificate for the same name.'),
            };
    }
};

// ⚠️ THE IN-USE REFUSAL IS NOT AN ERROR AND DOES NOT GO THROUGH THE CLASSIFIER.
//
// It arrives as a RESOLVED `DeleteCertificateResult` with `deleted: false` and
// `inUse: true` — never as a thrown GraphQL error — so it never reaches
// `classifyWriteError` at all. It gets its own copy because it is not a
// failure: the provider is telling us something true and useful, and the
// certificate is still doing its job.
export const deleteRefusedCopy = (
    result: { inUse: boolean, reason?: string | null },
): WriteErrorCopy => {

    if (result.inUse) {
        return {
            title: I18n.get('This certificate is in use'),
            action: I18n.get('The provider refused to delete it because something is currently serving traffic with it — a distribution, a load balancer or an API. This is not an error: deleting it would have taken that site offline. Move the resource to another certificate first, then delete this one. It is still listed and still working.'),
        };
    }

    // Refused for some other reason. The provider's own words carry it; this
    // says only what is certain, which is that the certificate is still there.
    return {
        title: I18n.get('The certificate was not deleted'),
        action: I18n.get('The provider refused the deletion and did not say it was in use. The certificate is unchanged and still listed — read the provider\'s message below.'),
    };
};
